import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  MenuBook as MenuBookIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { topicService } from '../services/topicService';
import DraggableTopicList from '../components/DraggableTopicList';
import AddTopicDialog from '../components/AddTopicDialog';
import AddQuestionDialog from '../components/AddQuestionDialog';
import MarkdownHelp from '../components/MarkdownHelp';
import CustomAppBar from '../components/CustomAppBar';
import { useParams, useNavigate } from 'react-router-dom';

// Token-based AND search helper
function matchesAllTokens(text, tokens) {
  const lowerText = (text || '').toLowerCase();
  return tokens.every(token => lowerText.includes(token));
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addTopicOpen, setAddTopicOpen] = useState(false);
  const [addQuestionOpen, setAddQuestionOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { subjectId } = useParams();

  const searchTokens = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);

  const filteredTopics = topics
    .map(topic => {
      const topicMatch = matchesAllTokens(topic.title, searchTokens) ||
        matchesAllTokens(topic.notes, searchTokens);
      const filteredQuestions = (topic.questions || []).filter(q => {
        const questionMatch = matchesAllTokens(q.questionText, searchTokens);
        const answerMatch = matchesAllTokens(q.answerText, searchTokens);
        return questionMatch || answerMatch;
      });
      if (searchQuery) {
        if (topicMatch) {
          // If topic matches, show all questions
          return { ...topic };
        } else if (filteredQuestions.length > 0) {
          // If only some questions match, show only those
          return { ...topic, questions: filteredQuestions };
        } else {
          // Neither topic nor questions match
          return null;
        }
      } else {
        // No search query, show all
        return { ...topic };
      }
    })
    .filter(Boolean);

  useEffect(() => {
    loadTopics();
  }, []);

  useEffect(() => {
    if (topics && topics.length > 0) {
      if (topics[0].questions && topics[0].questions.length > 0) {
      }
    }
  }, [topics]);

  useEffect(() => {
    if (filteredTopics && filteredTopics.length > 0) {
    }
  }, [filteredTopics]);

  const loadTopics = async () => {
    try {
      setLoading(true);
      const data = await topicService.getTopicsBySubjectId(subjectId);
      setTopics(data);
      setError('');
    } catch (error) {
      setError('Failed to load topics: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSubjects = () => {
    navigate('/subjects');
  };



  const handleAddTopic = async (topicData) => {
    try {
      const newTopic = await topicService.createTopic({...topicData, subjectId});
      setTopics([...topics, newTopic]);
      setAddTopicOpen(false);
    } catch (error) {
      setError('Failed to create topic: ' + error.message);
    }
  };

  const handleUpdateTopic = async (topicId, topicData) => {
    try {
      const updatedTopic = await topicService.updateTopic(topicId, topicData);
      setTopics(topics.map(topic => 
        topic.id === topicId ? updatedTopic : topic
      ));
    } catch (error) {
      setError('Failed to update topic: ' + error.message);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    try {
      await topicService.deleteTopic(topicId);
      setTopics(topics.filter(topic => topic.id !== topicId));
    } catch (error) {
      setError('Failed to delete topic: ' + error.message);
    }
  };

  const handleAddQuestion = async (questionData) => {
    try {
      const newQuestion = await topicService.addQuestion(selectedTopic.id, questionData);
      const updatedTopic = {
        ...selectedTopic,
        questions: [...selectedTopic.questions, newQuestion]
      };
      setTopics(topics.map(topic => 
        topic.id === selectedTopic.id ? updatedTopic : topic
      ));
      setSelectedTopic(updatedTopic);
      setAddQuestionOpen(false);
    } catch (error) {
      setError('Failed to add question: ' + error.message);
    }
  };

  const handleUpdateQuestion = async (topicId, questionData) => {
    try {
      const updatedQuestion = await topicService.updateQuestion(questionData.id, questionData);
      
      // Find the topic and update it
      const topicIndex = topics.findIndex(t => t.id === topicId);
      if (topicIndex !== -1) {
        const updatedTopic = {
          ...topics[topicIndex],
          questions: topics[topicIndex].questions.map(q => 
            q.id === questionData.id ? updatedQuestion : q
          )
        };
        
        const newTopics = [...topics];
        newTopics[topicIndex] = updatedTopic;
        setTopics(newTopics);
        
        // Update selectedTopic if it's the same topic
        if (selectedTopic && selectedTopic.id === topicId) {
          setSelectedTopic(updatedTopic);
        }
      }
    } catch (error) {
      setError('Failed to update question: ' + error.message);
    }
  };

  const handleDeleteQuestion = async (topicId, questionId) => {
    try {
      await topicService.deleteQuestion(questionId);
      
      // Find the topic and update it
      const topicIndex = topics.findIndex(t => t.id === topicId);
      if (topicIndex !== -1) {
        const updatedTopic = {
          ...topics[topicIndex],
          questions: topics[topicIndex].questions.filter(q => q.id !== questionId)
        };
        
        const newTopics = [...topics];
        newTopics[topicIndex] = updatedTopic;
        setTopics(newTopics);
        
        // Update selectedTopic if it's the same topic
        if (selectedTopic && selectedTopic.id === topicId) {
          setSelectedTopic(updatedTopic);
        }
      }
    } catch (error) {
      setError('Failed to delete question: ' + error.message);
    }
  };

  const handleUpdateTopicOrder = async (newOrder) => {
    try {
      await topicService.updateTopicOrder(newOrder.map(topic => topic.id));
      setTopics(newOrder);
    } catch (error) {
      setError('Failed to update topic order: ' + error.message);
    }
  };

  const handleQuestionReorder = async (topicId, reorderedQuestions) => {
    try {
      await topicService.updateQuestionOrder(topicId, reorderedQuestions.map(q => q.id));
      
      // Update the topic with reordered questions
      const updatedTopic = {
        ...selectedTopic,
        questions: reorderedQuestions
      };
      
      setTopics(topics.map(topic => 
        topic.id === topicId ? updatedTopic : topic
      ));
      
      if (selectedTopic && selectedTopic.id === topicId) {
        setSelectedTopic(updatedTopic);
      }
    } catch (error) {
      setError('Failed to update question order: ' + error.message);
    }
  };

  const handleUpdateQuestionAttempts = async (topicId, questionId, attempts) => {
    try {
      const updatedQuestion = await topicService.updateQuestionAttempts(questionId, attempts);
      
      // Find the topic and update the question with new attempts
      const topicIndex = topics.findIndex(t => t.id === topicId);
      if (topicIndex !== -1) {
        const updatedTopic = {
          ...topics[topicIndex],
          questions: topics[topicIndex].questions.map(q => 
            q.id === questionId ? updatedQuestion : q
          )
        };
        
        const newTopics = [...topics];
        newTopics[topicIndex] = updatedTopic;
        setTopics(newTopics);
        
        // Update selectedTopic if it's the same topic
        if (selectedTopic && selectedTopic.id === topicId) {
          setSelectedTopic(updatedTopic);
        }
      }
    } catch (error) {
      setError('Failed to update question attempts: ' + error.message);
      throw error; // Re-throw so QuestionItem can handle it
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <CustomAppBar>
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            '& .MuiAlert-icon': { fontSize: 24 }
          }} 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={handleBackToSubjects}
            sx={{
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'action.hover' },
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
              Your Topics
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
              Organize your topics and questions
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search topics, questions, answers..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, bgcolor: 'background.paper', minWidth: { xs: '100%', sm: 260 } }
            }}
            sx={{ mr: { xs: 0, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}
          />
          <MarkdownHelp />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddTopicOpen(true)}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              px: 3,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(37, 99, 235, 0.4)',
              }
            }}
          >
            Add Topic
          </Button>
        </Box>
      </Box>

      {filteredTopics.length === 0 ? (
        <Box 
            textAlign="center" 
            py={8}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 3,
              border: '2px dashed',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ mb: 3 }}>
              <MenuBookIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
            </Box>
            <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
              No topics yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
              Create your first topic to get started with your revision. Organize topics and questions by difficulty and track your progress.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddTopicOpen(true)}
              sx={{
                px: 3,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              Create Your First Topic
            </Button>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'grid', 
            gap: 3,
            gridTemplateColumns: { xs: '1fr', lg: '1fr' }
          }}>
            <DraggableTopicList
              topics={filteredTopics}
              onUpdate={handleUpdateTopic}
              onDelete={handleDeleteTopic}
              onAddQuestion={(topic) => {
                setSelectedTopic(topic);
                setAddQuestionOpen(true);
              }}
              onUpdateQuestion={handleUpdateQuestion}
              onDeleteQuestion={(topicId, questionId) => handleDeleteQuestion(topicId, questionId)}
              onReorder={handleUpdateTopicOrder}
              onReorderQuestions={handleQuestionReorder}
              onUpdateQuestionAttempts={handleUpdateQuestionAttempts}
            />
          </Box>
        )}

      {/* Add Topic Dialog */}
      <AddTopicDialog
        open={addTopicOpen}
        onClose={() => setAddTopicOpen(false)}
        onAdd={handleAddTopic}
      />

      {/* Add Question Dialog */}
      <AddQuestionDialog
        open={addQuestionOpen}
        onClose={() => setAddQuestionOpen(false)}
        onAdd={handleAddQuestion}
        topic={selectedTopic}
      />
    </CustomAppBar>
  );
};

export default Dashboard; 